import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Copy, Share2, Gift, TrendingUp, RefreshCw, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const Referral = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [referralCode, setReferralCode] = useState('');
  const [referralLink, setReferralLink] = useState('');
  const [stats, setStats] = useState({
    total_referrals: 0,
    active_referrals: 0,
    total_earned: 0,
    level1_referrals: 0,
    level2_referrals: 0
  });
  const [referrals, setReferrals] = useState([]);

  // Use relative URL - works on any domain
  const API_URL = '';

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Get referral code
      const codeRes = await axios.get(`${API_URL}/api/referral/my-referral-code`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferralCode(codeRes.data.referral_code);
      setReferralLink(codeRes.data.referral_link);
      
      // Get stats
      const statsRes = await axios.get(`${API_URL}/api/referral/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(statsRes.data);
      
      // Get referral history
      const historyRes = await axios.get(`${API_URL}/api/referral/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferrals(historyRes.data.referrals);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching referral data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load referral data',
        variant: 'destructive'
      });
      setLoading(false);
    }
  };

  const statsData = [
    { label: 'Total Referrals', value: stats.total_referrals.toString(), icon: Users, color: 'from-blue-400 to-blue-500' },
    { label: 'Active Referrals', value: stats.active_referrals.toString(), icon: TrendingUp, color: 'from-green-400 to-green-500' },
    { label: 'Total Earned', value: `$${stats.total_earned.toFixed(2)}`, icon: Gift, color: 'from-yellow-400 to-yellow-500' },
  ];

  const copyToClipboard = async (text) => {
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        toast({ title: 'Copied!', description: 'Referral code copied to clipboard' });
      } else {
        // Fallback to older method
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          document.execCommand('copy');
          toast({ title: 'Copied!', description: 'Referral code copied to clipboard' });
        } catch (err) {
          toast({ 
            title: 'Copy Failed', 
            description: 'Please copy the code manually', 
            variant: 'destructive' 
          });
        } finally {
          textArea.remove();
        }
      }
    } catch (err) {
      // If modern clipboard fails, try fallback
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        document.execCommand('copy');
        toast({ title: 'Copied!', description: 'Referral code copied to clipboard' });
      } catch (fallbackErr) {
        toast({ 
          title: 'Copy Failed', 
          description: 'Please copy the code manually', 
          variant: 'destructive' 
        });
      } finally {
        textArea.remove();
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      <div className="sticky top-0 bg-slate-900/95 backdrop-blur-sm border-b border-cyan-500/20 z-10">
        <div className="flex items-center justify-between px-4 py-4">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-semibold">Back</span>
          </button>
          <h1 className="text-lg font-bold text-cyan-400">Referral Program</h1>
          <div className="w-16"></div>
        </div>
      </div>

      <div className="px-4 py-6">
        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <RefreshCw className="w-8 h-8 text-cyan-400 animate-spin" />
          </div>
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
              {statsData.map((stat, idx) => {
                const Icon = stat.icon;
                return (
                  <div key={idx} className="bg-slate-800/50 border border-cyan-500/20 rounded-xl p-4 sm:p-3 text-center">
                    <div className={`w-12 h-12 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br ${stat.color} flex items-center justify-center mx-auto mb-2`}>
                      <Icon className="w-6 h-6 sm:w-5 sm:h-5 text-white" />
                    </div>
                    <div className="text-2xl sm:text-xl font-bold text-cyan-400">{stat.value}</div>
                    <div className="text-sm sm:text-xs text-cyan-700">{stat.label}</div>
                  </div>
                );
              })}
            </div>

            {/* Program Info */}
            <div className="bg-gradient-to-r from-green-900/30 to-emerald-900/30 border border-green-500/30 rounded-xl p-4 mb-6">
              <h3 className="text-base font-bold text-green-400 mb-2 flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Referral Rewards
              </h3>
              <div className="space-y-1 text-sm text-gray-300">
                <p>💰 <span className="text-green-400 font-semibold">$10 instant bonus</span> when referee deposits $1,000+</p>
                <p>📈 <span className="text-green-400 font-semibold">1% commission</span> on all referee trading fees</p>
                <p>🎁 <span className="text-purple-400 font-semibold">Level 2 bonus:</span> Earn 50% from your referrals' referrals</p>
                <p className="text-xs text-gray-400 mt-2">Max $1,000 per referral • Instant credit to wallet</p>
              </div>
            </div>
          </>
        )}

        {/* Referral Code */}
        {!loading && (
          <div className="bg-gradient-to-r from-cyan-900/30 to-blue-900/30 border border-cyan-500/30 rounded-xl p-4 sm:p-6 mb-6 neon-border">
            <h3 className="text-base sm:text-lg font-bold text-cyan-400 mb-4">Your Referral Code</h3>
            <div className="bg-slate-900/50 rounded-lg p-4 sm:p-3 mb-3">
              <div className="text-center">
                <div className="text-xl sm:text-2xl font-bold text-cyan-300 mb-2 break-all">{referralCode || 'Loading...'}</div>
                <div className="text-xs text-cyan-700">Share this code with friends</div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                onClick={() => copyToClipboard(referralCode)}
                disabled={!referralCode}
                className="flex-1 h-11 sm:h-10 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 border border-cyan-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Code
              </Button>
              <Button
                onClick={() => copyToClipboard(referralLink)}
                disabled={!referralLink}
                className="flex-1 h-11 sm:h-10 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 border border-cyan-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share Link
              </Button>
            </div>
          </div>
        )}

        {/* Referral List */}
        {!loading && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-cyan-400">Your Referrals</h3>
              <button 
                onClick={fetchReferralData}
                className="p-2 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 transition-colors"
              >
                <RefreshCw className="w-4 h-4 text-cyan-400" />
              </button>
            </div>
            
            {referrals.length === 0 ? (
              <div className="bg-slate-800/50 border border-cyan-500/20 rounded-xl p-8 text-center">
                <Users className="w-12 h-12 text-cyan-400/50 mx-auto mb-3" />
                <p className="text-gray-400">No referrals yet</p>
                <p className="text-xs text-gray-500 mt-2">Share your code to start earning!</p>
              </div>
            ) : (
              referrals.map((ref, idx) => (
                <div key={idx} className="bg-slate-800/50 border border-cyan-500/20 rounded-xl p-4 mb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <div className="font-semibold text-cyan-300">{ref.referee_username}</div>
                        {ref.level === 2 && (
                          <span className="px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 text-xs">
                            Level 2
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-cyan-700">
                        {ref.created_at ? new Date(ref.created_at).toLocaleDateString() : 'N/A'}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-green-400">${ref.total_earned.toFixed(2)}</div>
                      <div className={`text-xs ${
                    ref.status === 'active' ? 'text-green-400' : 
                    ref.status === 'pending' ? 'text-yellow-400' : 'text-gray-500'
                  }`}>
                    {ref.status}
                  </div>
                </div>
              </div>
            </div>
          ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Referral;